import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef } from "react";
import { oneOf, string, bool, oneOfType, elementType, func } from "prop-types";
import { classes } from "../utils/classes";
import pureFC from "../utils/pureFC";
import fontWeight from "../utils/fontWeight";
import pureProps, { deepMergeProps } from "../utils/pureProps";
import { spacingClassName, spacingProps, spacingPropTypes } from "../props/spacing";
import { colorPropTypes, sxClassName, sxProps, sxPropTypes } from "../props/sx";
import { utilityClassName, utilityProps, utilityPropTypes } from "../props/utility";
import { sizingClassName, sizingProps, sizingPropTypes } from "../props/sizing";
import { prevent } from "../utils/handlers";
import RouterProvider from "../utils/RouterProvider";
import { isAnchorLink } from "../utils/isAnchorLink";
import { smoothScrollTo } from "../utils/smoothScrollTo";
import { isExternalLink } from "../utils/isExternalLink";

const Link = ({
	Tag = "a",
	isBaseClassName = true,
	external = false,
	size,
	weight,
	color,
	hoverColor,
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	sizing = { ...sizingProps },
	className,
	href,
	as,
	active,
	onClick,
	...props
}) => {
	const headerRef = useRef(null);
	const routerPush = useContext(RouterProvider.context);
	const sizeProps = size ? { fontSize: size } : {};
	const weightProps = weight ? { fontWeight: weight } : {};
	const defaultColorProps = color ? { fontColor: color } : { fontColor: null };
	const defaultColorHoverProps = hoverColor ? { fontColor: hoverColor } : { fontColor: null };
	const colorProps = { ...defaultColorProps, hover: { ...defaultColorHoverProps } };

	useLayoutEffect(() => {
		const navbar = document.querySelector(".header");

		if (!navbar) return;

		const computedStyle = window.getComputedStyle(navbar);
		const navbarPosition = computedStyle?.getPropertyValue("position");
		const isStickyNavbar = navbarPosition === "sticky";

		if (!isStickyNavbar) return;

		headerRef.current = navbar;
	}, []);

	const classNameList = useMemo(
		() =>
			classes(
				isBaseClassName && "link",
				external && "link--external",
				...utilityClassName(utility),
				...sxClassName(
					deepMergeProps({ ...sizeProps, ...weightProps, ...colorProps }, pureProps(sx)),
				),
				...spacingClassName(spacing),
				...sizingClassName(sizing),
				className,
			),
		[sizeProps, weightProps, colorProps],
	);

	const followLink = useCallback(
		event => {
			if (typeof onClick === "function") {
				onClick(event);
				if (event.defaultPrevented) return;
			}

			if (isAnchorLink(href)) smoothScrollTo(href, headerRef.current?.offsetHeight);
			if (typeof routerPush === "function") {
				// router.push correctly handles both internal and external links
				prevent(event);
				routerPush(href, as, { scroll: false });
			}
		},
		[active, onClick, href, as, routerPush],
	);

	const handleClick =
		// prettier-ignore
		// eslint-disable-next-line no-nested-ternary
		active ? prevent :
		!isExternalLink(href) && href?.trim() && href !== "#" ? followLink :
		onClick ?? null;

	const classNameProp = classNameList ? { className: classNameList } : {};
	return <Tag href={href} {...props} {...classNameProp} onClick={handleClick} />;
};

Link.propTypes = {
	Tag: oneOfType([oneOf(["a", "button"]), elementType]),
	isBaseClassName: bool,
	external: bool,
	size: oneOf(["xxxl", "xxl", "xl", "l", "m", "s", "xs"]),
	weight: oneOf(Object.keys(fontWeight)),
	color: colorPropTypes,
	hoverColor: colorPropTypes,
	utility: utilityPropTypes,
	sx: sxPropTypes,
	sizing: sizingPropTypes,
	spacing: spacingPropTypes,
	className: string,
	href: string,
	as: string,
	active: bool,
	onClick: func,
};

const LinkButton = props => <Link Tag="button" type="button" {...props} />;

Link.Button = LinkButton;

export default pureFC(Link);
